import type {
  KeyboardNavigation,
  ListValue,
  StatefulList,
  ValueListItem,
} from '@xemplo/stateful-list-core';

export const ListMode = {
  Single: 'single',
  Multi: 'multi',
  Tags: 'tags',
} as const;
export type ListMode = (typeof ListMode)[keyof typeof ListMode];

type BaseContext = KeyboardNavigation & {
  isNoResult: boolean;
  items: StatefulList;
  renderItems: StatefulList;
  useSearch: boolean;
  search: string;
  isSelected: (value: ListValue) => boolean;
  setSearch: (search: string) => void;
  toggleItem: (value: ListValue) => void;
};

export type MultiSelectContextType = BaseContext & {
  isAllSelected: boolean;
  indeterminate: boolean;
  selectedItems: Set<ListValue>;
  valueItems: ValueListItem[];
  useSelectAll: boolean;
  selectAll: () => void;
};

export type SingleSelectContextType = BaseContext & {
  selectedItem: ListValue | undefined;
  autoScrollToSelected: boolean;
};

export type TagCreateFn = (tag: string) => void;

export type TagsSelectContextType = BaseContext & {
  selectedItems: Set<ListValue>;
  valueItems: ValueListItem[];
  search: string;
  maxTagChar: number;
  validCharRegex: RegExp;
  onTagCreate?: (tag: string) => void;
};

export type StatefulListInputProps = {
  /** The ID to be set on the listbox element for ARIA relationships */
  id?: string;

  /** Controls the width of the container */
  width?: string | number;

  /**
   * Controls the height of the container.
   * The height must be a number due to offset
   * calculations for the scroll area.
   * @default 300
   */
  height?: number;

  /**
   * Items to render in the list.
   * Must contain at least one group with at least one item.
   */
  items: StatefulList;

  /**
   * Appends search field into the list
   * @default false when SingleSelect, true when MultiSelect
   */
  useSearch?: boolean;

  /**
   * ClassName to be added to the list wrapper element.
   * This allow re-styling of the component from a parent component.
   */
  className?: string;

  /**
   * In some cases, we want the list to have the same width
   * as the trigger element. e.g.: when the trigger is an input
   * and we want the list to be as wide as the input.
   *
   * Without this prop, the width needs to be set manually via
   * `width` prop. If both are provided, if the `width` prop is a number,
   * it will be used, otherwise the width of the trigger element will be used.
   */
  triggerId?: string;
};

export type ListItemProps = {
  item: ValueListItem;
  className?: string;
};

/** Describes the list of css variables used by the component */
export const ListCssVar = {
  WrapperHeight: '--list-wrapper-height',
  WrapperWidth: '--list-wrapper-width',
  ScrollAreaHeight: '--list-scrollarea-height',
} as const;
export type ListCssVar = (typeof ListCssVar)[keyof typeof ListCssVar];
